import moment from "moment";
import i18n from "@/utils/i18n";
import "moment/locale/tr";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/utils";
import type {
  Poll,
  PollFilters,
  PollDetail,
  PollAttendee,
  GetAttendeesParams,
  PollQuestion,
  SurveyResDto,
  SurveyResDtoPaginatedResponse,
  SurveyQuestionResDto,
  SurveyAttemptDetailResDto,
  AdminSurveysQueryParams,
  CreateSurveyDto,
  UpdateSurveyDto,
} from "../types/polls.types";
import { SurveyStatus, SurveyQuestionType } from "../types/polls.types";

const ADMIN_SURVEYS_ENDPOINT = "/api/admin/surveys";

function mapSurveyStatusToPollStatus(status?: SurveyStatus): Poll["status"] {
  switch (status) {
    case SurveyStatus.DRAFT:
      return "taslak";
    case SurveyStatus.ASSIGNED:
      return "atanmis";
    case SurveyStatus.UNASSIGNED:
      return "atanmamis";
    case SurveyStatus.ARCHIVE:
      return "arsiv";
    default:
      return "atanmis";
  }
}

/** Backend SurveyQuestionType (1-6) -> PollQuestion.questionType */
function mapSurveyQuestionTypeToPoll(
  type?: SurveyQuestionType | number,
): PollQuestion["questionType"] {
  switch (type) {
    case SurveyQuestionType.TEXT:
      return "text";
    case SurveyQuestionType.SINGLE_CHOICE:
      return "single_choice";
    case SurveyQuestionType.MULTIPLE_CHOICE:
      return "multiple_choice";
    case SurveyQuestionType.DROPDOWN:
      return "dropdown";
    case SurveyQuestionType.RATING:
      return "rating";
    case SurveyQuestionType.SCALE:
      return "scale";
    default:
      return "text";
  }
}

function mapPollStatusToSurveyStatus(
  status?: PollFilters["status"],
): SurveyStatus | undefined {
  if (!status) return undefined;
  switch (status) {
    case "taslak":
      return SurveyStatus.DRAFT;
    case "atanmis":
      return SurveyStatus.ASSIGNED;
    case "atanmamis":
      return SurveyStatus.UNASSIGNED;
    case "arsiv":
      return SurveyStatus.ARCHIVE;
    default:
      return undefined;
  }
}

const normalizeDatePart = (value?: string | null) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return trimmed.includes("T") ? trimmed.split("T")[0] : trimmed;
};

/** endDate'den bugünü çıkararak kalan süreyi hesaplar (moment ile) */
function getRemainingTime(endDate?: string | null): string {
  const normalized = normalizeDatePart(endDate);
  if (!normalized) return "-";
  const end = moment(normalized, "YYYY-MM-DD");
  const now = moment();
  if (!end.isValid()) return "-";
  const daysLeft = end.endOf("day").diff(now, "days");
  if (daysLeft < 0) return i18n.t("pollsApi.expired");
  if (daysLeft === 0) return i18n.t("pollsApi.today");
  if (daysLeft === 1) return i18n.t("pollsApi.oneDay");
  return `${daysLeft} Gün`;
}

function mapSurveyResDtoToPoll(item: SurveyResDto): Poll {
  const pollName = item.name ?? item.relatedEducation?.name ?? "-";
  const relatedEducation = item.relatedEducation?.name ?? "-";
  const remainingTime = getRemainingTime(item.endDate);
  const total = item.totalEnrolledCount ?? 0;
  const score = item.participatedPercentage ?? 0;
  const participatedCount = item.participatedCount ?? 0;
  const percentage =
    participatedCount > 0 ? Math.round((participatedCount / total) * 100) : 0;
  return {
    id: item.id,
    pollName,
    publishDate: formatDate(item.createdAt),
    relatedEducation,
    participation: {
      score,
      total,
      percentage,
      participatedCount,
    },
    remainingTime,
    status: mapSurveyStatusToPollStatus(item.status),
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    participatedCount: item.participatedCount ?? 0,
    educations: item.educations ?? [],
  };
}

/**
 * GET /api/admin/surveys
 * Admin anket listesi (sayfalı).
 * Query: page, limit (Swagger)
 */
export const pollsApi = {
  getAll: async (params?: {
    page?: number;
    pageSize?: number;
    filters?: PollFilters;
  }): Promise<{ data: Poll[]; total: number }> => {
    const educationIds = params?.filters?.educationIds;
    const apiParams: AdminSurveysQueryParams = {
      page: params?.page ?? 1,
      limit: params?.pageSize ?? 10,
      search: params?.filters?.search,
      status: mapPollStatusToSurveyStatus(params?.filters?.status),
      educationIds:
        Array.isArray(educationIds) && educationIds.length > 0
          ? educationIds.filter((id): id is string => !!id)
          : undefined,
      sortBy: params?.filters?.sortBy,
      sortOrder: params?.filters?.sortOrder,
    };

    const { data } = await apiClient.get<SurveyResDtoPaginatedResponse>(
      ADMIN_SURVEYS_ENDPOINT,
      { params: apiParams },
    );

    const rawItems = Array.isArray(data?.data) ? data.data : [];
    const count = data?.count ?? 0;

    return {
      data: rawItems.map(mapSurveyResDtoToPoll),
      total: count,
    };
  },

  getById: async (id: string): Promise<Poll> => {
    const { data } = await apiClient.get<{ data: SurveyResDto }>(
      `${ADMIN_SURVEYS_ENDPOINT}/${id}`,
    );
    if (!data?.data) {
      throw new Error("Poll not found");
    }
    return mapSurveyResDtoToPoll(data.data);
  },

  /**
   * GET /api/admin/surveys/{id}
   * Anket detayı + katılım istatistikleri (attempts ile)
   */
  getDetailById: async (id: string): Promise<PollDetail> => {
    const surveyRes = await apiClient.get<{ data: SurveyResDto }>(
      `${ADMIN_SURVEYS_ENDPOINT}/${id}`,
    );

    const surveyData = surveyRes.data?.data;
    if (!surveyData) {
      throw new Error("Poll not found");
    }

    const poll = mapSurveyResDtoToPoll(surveyData);
    // Katılım: surveys API'den gelen totalEnrolledCount ve participatedCount
    // (participatedPercentage 0-100'lük yüzde değeridir; UI bunu adet
    // gibi gösterirse "5/20" yerine "60/20" gibi yanlış değerler çıkar.)
    const totalAttendees = surveyData.totalEnrolledCount ?? 0;
    const completedAttendees = surveyData.participatedCount ?? 0;

    const rawQuestions = (surveyData.questions ?? []) as SurveyQuestionResDto[];
    const questions: PollQuestion[] = rawQuestions.map((q) => {
      const questionType = mapSurveyQuestionTypeToPoll(q.type);
      const options = Array.isArray(q.options)
        ? q.options
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((opt) => opt.optionText ?? "")
        : [];
      return {
        id: q.id ?? "",
        questionText: q.questionText ?? "",
        questionType,
        options,
        required: q.isRequired ?? true,
      };
    });

    const startDateStr = surveyData.startDate
      ? formatDate(normalizeDatePart(surveyData.startDate) ?? "")
      : "-";
    const endDateStr = surveyData.endDate
      ? formatDate(normalizeDatePart(surveyData.endDate) ?? "")
      : "-";

    return {
      ...poll,
      description: surveyData.description ?? "",
      startDate: surveyData.startDate
        ? startDateStr
        : formatDate(surveyData.createdAt),
      endDate: endDateStr,
      questions,
      totalAttendees,
      completedAttendees,
    };
  },

  /**
   * GET /api/admin/surveys/:surveyId/attempts
   * Anket katılımcıları listesi (paginationsız, filtre ve sıralama destekli).
   */
  getAttendees: async (
    surveyId: string,
    params?: GetAttendeesParams,
  ): Promise<{ data: PollAttendee[]; total: number }> => {
    type AttemptItem = {
      id?: string | null;
      userID?: string;
      user?: {
        firstName?: string;
        lastName?: string;
        department?: { id?: string; name?: string };
      };
      startedAt?: string;
      finishedAt?: string | null;
      answeredQuestionCount?: number;
      totalQuestionCount?: number;
      educationId?: string | null;
      educationName?: string | null;
    };

    type AttemptsResponse = {
      count: number;
      participatedCount: number;
      notParticipatedCount: number;
      items: AttemptItem[];
    };

    const apiParams: Record<string, unknown> = {};
    if (params?.search?.trim()) apiParams.search = params.search.trim();
    if (params?.sortBy) apiParams.sortBy = params.sortBy;
    if (params?.sortOrder) apiParams.sortOrder = params.sortOrder;
    if (params?.completed !== undefined) apiParams.completed = params.completed;
    if (params?.answerRateMin !== undefined)
      apiParams.answerRateMin = params.answerRateMin;
    if (params?.answerRateMax !== undefined)
      apiParams.answerRateMax = params.answerRateMax;

    type ApiResponse = {
      success?: boolean;
      status?: number;
      data: AttemptsResponse;
      message?: string;
    };

    const { data: rawResponse } = await apiClient.get<ApiResponse>(
      `${ADMIN_SURVEYS_ENDPOINT}/${surveyId}/attempts`,
      {
        params: Object.keys(apiParams).length > 0 ? apiParams : undefined,
      },
    );

    const attemptsData = rawResponse?.data;
    const items: AttemptItem[] = Array.isArray(attemptsData?.items)
      ? attemptsData.items
      : [];
    const count = attemptsData?.count ?? items.length;

    const mapped: PollAttendee[] = items.map((a, i) => {
      const name =
        a.user?.firstName && a.user?.lastName
          ? `${a.user.firstName} ${a.user.lastName}`
          : i18n.t("pollsApi.participantFallback");
      const answered = !!a.finishedAt;
      const totalQuestions = a.totalQuestionCount ?? 1;
      const answeredCount =
        a.answeredQuestionCount !== undefined
          ? a.answeredQuestionCount
          : answered
            ? 1
            : 0;
      const percentage =
        totalQuestions > 0
          ? Math.round((answeredCount / totalQuestions) * 100)
          : 0;
      return {
        id: a.id ?? `attendee-${i + 1}`,
        name,
        title: a.user?.department?.name ?? "-",
        status: answered ? "katildi" : "katilmadi",
        answeredCount,
        totalQuestions,
        percentage,
        educationId: a.educationId ?? null,
        educationName: a.educationName ?? null,
      };
    });

    return { data: mapped, total: count ?? 0 };
  },

  /**
   * GET /api/admin/surveys/survey-attempts/{attemptId}/answers
   * Anket denemesi cevaplarını getir.
   */
  getAttemptAnswers: async (
    attemptId: string,
  ): Promise<SurveyAttemptDetailResDto> => {
    const { data } = await apiClient.get<{ data: SurveyAttemptDetailResDto }>(
      `${ADMIN_SURVEYS_ENDPOINT}/survey-attempts/${attemptId}/answers`,
    );
    if (!data?.data) {
      throw new Error("Attempt answers not found");
    }
    return data.data;
  },

  /**
   * POST /api/admin/surveys
   * Yeni anket oluştur.
   */
  create: async (payload: CreateSurveyDto): Promise<SurveyResDto> => {
    const { data } = await apiClient.post<{ data: SurveyResDto }>(
      ADMIN_SURVEYS_ENDPOINT,
      payload,
    );
    if (!data?.data) {
      throw new Error("Survey create response is invalid");
    }
    return data.data;
  },

  /**
   * PATCH /api/admin/surveys/{id}
   * Anketi güncelle.
   * - questions gönderilir ve length >= 1: Mevcut sorular silinir, gönderilenler eklenir
   * - questions gönderilmez: Sorular aynen kalır, sadece name/description/startDate/endDate güncellenir
   */
  update: async (
    id: string,
    payload: UpdateSurveyDto,
  ): Promise<SurveyResDto> => {
    const { data } = await apiClient.patch<{ data: SurveyResDto }>(
      `${ADMIN_SURVEYS_ENDPOINT}/${id}`,
      payload,
    );
    if (!data?.data) {
      throw new Error("Survey update response is invalid");
    }
    return data.data;
  },

  /**
   * DELETE /api/admin/surveys/{id}
   * Anketi sil (soft delete).
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${ADMIN_SURVEYS_ENDPOINT}/${id}`);
  },

  /**
   * PATCH /api/admin/surveys/{id}/questions/reorder
   * Anket sorularını sırala. orderedIds tüm aktif soru ID'lerini
   * içermek zorunda — partial reorder desteklenmiyor.
   */
  reorderQuestions: async (
    surveyId: string,
    orderedIds: string[],
  ): Promise<void> => {
    await apiClient.patch(
      `${ADMIN_SURVEYS_ENDPOINT}/${surveyId}/questions/reorder`,
      { orderedIds },
    );
  },
};

/** Frontend QuestionType -> Backend SurveyQuestionType mapping */
export const mapQuestionTypeToBackend = (type: string): SurveyQuestionType => {
  const mapping: Record<string, SurveyQuestionType> = {
    text: SurveyQuestionType.TEXT,
    single_choice: SurveyQuestionType.SINGLE_CHOICE,
    multiple_choice: SurveyQuestionType.MULTIPLE_CHOICE,
    dropdown: SurveyQuestionType.DROPDOWN,
    star_rating: SurveyQuestionType.RATING,
    scale: SurveyQuestionType.SCALE,
  };
  return mapping[type] ?? SurveyQuestionType.TEXT;
};
